/**
 * PHARMAKON — Cross-module X · Caisse libre windows (brief §10, §10.3), browser twin of the server CONFIG.
 *
 * The multiwindow store and the window tabs (XWP-18) use these values for immediate feedback: the
 * « Nouvelle fenêtre » key is disabled at `max_actives`, a quantity edit commits after
 * `debounce_quantite_ms`, the tab reads « Expirée — réservations libérées ». The server
 * (`apps/server/src/services/caisse-fenetre.service.js`) remains the authority.
 *
 * Owner of the values: `apps/server/src/config/caisse-fenetres.config.js`. This file is a twin, not
 * an import, because the API image is built from apps/server alone. The server test
 * `src/__tests__/caisse-fenetres.parity.test.js` imports both and fails on any drift — change a
 * value there first, then here.
 */

/** Multiwindow knobs (XD-24, XD-47). */
export const CAISSE_FENETRES = Object.freeze({
  max_actives: 3,
  ttl_inactivite_minutes: 30,
  debounce_quantite_ms: 400,
  max_lignes: 100,
  quantite_max: 9_999,
  expirees_listees_max: 10,
  notes_max: 500,
});

/** Lifecycle of a window (Prisma enum `StatutFenetreCaisse`). */
export const STATUT_FENETRE_CAISSE = Object.freeze({
  ACTIVE: "ACTIVE",
  VALIDEE: "VALIDEE",
  ANNULEE: "ANNULEE",
  EXPIREE: "EXPIREE",
  FERMEE_SESSION: "FERMEE_SESSION",
});

/** Lifecycle of a caisse hold (Prisma enum `StatutReservationLot`). */
export const STATUT_RESERVATION_LOT = Object.freeze({
  ACTIVE: "ACTIVE",
  CONVERTIE: "CONVERTIE",
  LIBEREE: "LIBEREE",
  EXPIREE: "EXPIREE",
});

/**
 * The window's remise modes (X §9, XWP-07): a rate (≤ pourcentage_max) or a fixed DA amount, whose
 * ceiling (brut × pourcentage_max / 100) is checked on the server-priced brut when the window is
 * validated into a sale. The SPA reads the live ceiling from GET /ventes/referentiel.
 */
export const REMISE_FENETRE = Object.freeze({
  modes: Object.freeze(["POURCENTAGE", "MONTANT"]),
  pourcentage_max: 30,
  montant_max_da: 10000000,
});

/** The client-proposed basket line key (VarChar(40)). */
export const CLE_LIGNE_FENETRE = Object.freeze({ max: 40, motif: "^[A-Za-z0-9_-]{1,40}$" });

/** French texts of the window / reservation states and refusals. */
export const MESSAGES_FENETRES = Object.freeze({
  LIMITE: "Trois fenêtres sont déjà ouvertes. Terminez ou fermez une vente.",
  NON_ACTIVE: "Cette fenêtre n'est plus active.",
  EXPIREE: "Expirée — réservations libérées",
  VERSION: "La fenêtre a été modifiée ailleurs (autre onglet). Rechargez-la.",
  SESSION_REMPLACEE: "Cette session de caisse a été remplacée par une connexion plus récente. Rechargez la page.",
  FENETRE_INTROUVABLE: "Fenêtre introuvable",
  LIGNE_INTROUVABLE: "Ligne introuvable dans cette fenêtre",
  LIGNE_EXISTANTE: "Cette ligne existe déjà dans la fenêtre.",
  LIGNES_MAX: `Une fenêtre contient au plus ${CAISSE_FENETRES.max_lignes} lignes.`,
  STOCK_INSUFFISANT: "Stock insuffisant",
  PRODUIT_INTROUVABLE: "Produit introuvable",
  PRODUIT_BLOQUE: "Ce produit est bloqué à la vente.",
  VENTE_LIBRE_INTERDITE: "Ce produit ne peut pas être vendu en vente libre.",
  LOT_PERIME: "Lot périmé — choisissez un autre lot (F7).",
  CLIENT_INTROUVABLE: "Client introuvable",
  INCOHERENCE_CACHE: "Incohérence du cache de réservation du lot — opération annulée.",
  // XWP-18 — validation of a window, line extras, draft resumption
  PANIER_VIDE: "La fenêtre est vide — ajoutez un produit avant de valider.",
  RESERVATION_INCOMPLETE: "La réservation d'une ligne ne couvre plus sa quantité — rechargez la fenêtre.",
  LOT_PERIME_DEPUIS_AJOUT: "Lot périmé depuis l'ajout — choisissez un autre lot (F7).",
  CLIENT_REQUIS_CREDIT: "Veuillez sélectionner un client pour un crédit.",
  SECOURS_CREDIT: "Une ligne de secours ne peut pas partir en crédit — encaissez-la comptant ou retirez-la.",
  SECOURS_SANS_LOT: "Une ligne de secours n'a pas de lot en stock à changer.",
  LIGNE_AUCUN_CHANGEMENT: "Aucun changement à enregistrer sur la ligne.",
  // X XWP-98 review — the way a line's lot is changed (LIGNE | AUTRES_LOTS)
  VOIE_CHOIX_LOT_INVALIDE: "Changement de lot : la voie doit être LIGNE ou AUTRES_LOTS.",
  // X XWP-98 review — two counter operations raced on the same lots and this one could not finish
  CONFLIT_CONCURRENT: "Une autre caisse vient de modifier ce stock : réessayez.",
  BROUILLON_FENETRE_NON_VIDE: "Reprenez la vente en attente dans une fenêtre vide (F3 « Nouvelle fenêtre »).",
  BROUILLON_SECOURS: "Cette vente en attente contient une ligne de secours : elle ne peut pas être reprise en fenêtre. Supprimez-la puis ressaisissez le panier.",
  BROUILLON_VIDE: "Cette vente en attente ne contient aucune ligne.",
});

/**
 * XWP-18 — the outcome of a window validation (`POST /caisse/fenetres/:id/valider`): a cash sale
 * (espèces / carte / chèque / virement, the vente libre path) or a crédit (deferred payment, the
 * crédit path). CHIFA and « instance à terme » hand the basket to Vente CHIFA instead: the window
 * is closed first (its holds released — the CHIFA page has no reservation model, XD-49).
 */
export const ISSUE_FENETRE = Object.freeze({ COMPTANT: "COMPTANT", CREDIT: "CREDIT" });
